import { useCallback, useMemo } from 'react';

import { FormActionType, FormType, ServiceModalName } from '@enums';
import { useAppDispatch, useFormSubmissionStatus } from '@hooks';
import { addServiceModal, updateFormProcedure } from '@store';
import type { FormConfig, ModalSizes, ServiceModalPayload } from '@types';

interface UseCreateModalConfig {
  modalName?: ServiceModalName;
  formType: FormType;
  formConfig: FormConfig;
  initialValues?: Record<string, any>;
  handleSubmit: (values: any) => void;
  handleCancel?: () => void;
  modalSize?: ModalSizes;
  actionType?: FormActionType;
  buttonLoading?: boolean;
  disabled?: boolean;
  customModalOptions?: Partial<ServiceModalPayload['option']>;
  customLabelOptions?: Partial<ServiceModalPayload['label']>;
}

interface UseCreateModalReturn {
  openModal: () => void;
  isDisabled: boolean;
  isSubmitting: boolean;
}

export const useCreateModal = (config: UseCreateModalConfig): UseCreateModalReturn => {
  const dispatch = useAppDispatch();

  const {
    modalName = ServiceModalName.FormModal,
    formType,
    formConfig,
    initialValues = {},
    handleSubmit,
    handleCancel,
    modalSize = 'md',
    actionType = FormActionType.Create,
    buttonLoading = false,
    disabled = false,
    customModalOptions = {},
    customLabelOptions = {},
  } = config;

  const { inputList = [], formActionType, label, validationSchema, endpoint } = formConfig;
  const isSubmitting = useFormSubmissionStatus({ formType, endpoint });

  const handleModalCancel = useCallback(() => {
    dispatch(updateFormProcedure({ formType, procedure: FormActionType.Idle }));
    handleCancel?.();
  }, [formType, handleCancel, dispatch]);

  const memoizedHandleSubmit = useCallback(
    (values: any) => {
      if (disabled) return;
      handleSubmit(values);
    },
    [handleSubmit, disabled],
  );

  const modalPayload = useMemo(
    (): ServiceModalPayload => ({
      label: {
        ...label,
        text: label?.description,
        ...customLabelOptions,
      },
      option: {
        modalForm: {
          initialValues,
          inputList,
          validationSchema,
          enableReinitialize: true,
        },
        modalTable: {
          formType,
          endpoint,
        },
        button: {
          loading: buttonLoading,
          disabled,
          show: {
            confirmButton: true,
            cancelButton: true,
            closeButton: false,
          },
        },
        size: modalSize,
        ...customModalOptions,
      },
      callbackFunction: {
        submit: memoizedHandleSubmit,
        cancel: handleModalCancel,
      },
    }),
    [
      label,
      customLabelOptions,
      initialValues,
      inputList,
      validationSchema,
      formType,
      endpoint,
      modalSize,
      buttonLoading,
      disabled,
      customModalOptions,
      memoizedHandleSubmit,
      handleModalCancel,
    ],
  );

  const openModal = useCallback(() => {
    if (disabled) return;

    dispatch(updateFormProcedure({ formType, procedure: formActionType ?? actionType }));
    dispatch(
      addServiceModal({
        name: modalName,
        payload: modalPayload,
      }),
    );
  }, [modalName, modalPayload, formType, actionType, disabled, dispatch]);

  return {
    openModal,
    isDisabled: disabled,
    isSubmitting,
  };
};
